// GLM → Kilo flow builder. Trigger/action values mirror the backend
// GLMKiloFlowTriggers / GLMKiloFlowActions enums (snake_case).

export enum GLMKiloFlowTriggers {
  PIPELINE_STAGE_MOVEMENT = 'pipeline_stage_movement',
  TAG_ADDED = 'tag_added',
  CONTACT_UPDATED = 'contact_updated',
  NOTE_UPSERTED = 'note_upserted',
  NOTE_DELETED = 'note_deleted',
}

export enum GLMKiloFlowActions {
  LINK_ATHLETE_AND_SET_STATUS = 'link_athlete_and_set_status',
  SYNC_ATHLETE_DATA = 'sync_athlete_data',
  UPSERT_NOTE = 'upsert_note',
  DELETE_NOTE = 'delete_note',
}

// Action is auto-set per trigger — the user never chooses it.
export const ACTION_BY_TRIGGER: Record<GLMKiloFlowTriggers, GLMKiloFlowActions> = {
  [GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT]: GLMKiloFlowActions.LINK_ATHLETE_AND_SET_STATUS,
  [GLMKiloFlowTriggers.TAG_ADDED]: GLMKiloFlowActions.LINK_ATHLETE_AND_SET_STATUS,
  [GLMKiloFlowTriggers.CONTACT_UPDATED]: GLMKiloFlowActions.SYNC_ATHLETE_DATA,
  [GLMKiloFlowTriggers.NOTE_UPSERTED]: GLMKiloFlowActions.UPSERT_NOTE,
  [GLMKiloFlowTriggers.NOTE_DELETED]: GLMKiloFlowActions.DELETE_NOTE,
};

// Lifecycle triggers link the athlete↔contact and set a status (ac_athlete_status).
export const LIFECYCLE_TRIGGERS = new Set<GLMKiloFlowTriggers>([
  GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT,
  GLMKiloFlowTriggers.TAG_ADDED,
]);

export const TRIGGER_LABELS: Record<GLMKiloFlowTriggers, string> = {
  [GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT]: 'A contact moves to a pipeline stage',
  [GLMKiloFlowTriggers.TAG_ADDED]: 'A tag is added to a contact',
  [GLMKiloFlowTriggers.CONTACT_UPDATED]: "A contact's data is updated",
  [GLMKiloFlowTriggers.NOTE_UPSERTED]: 'A note is created or updated',
  [GLMKiloFlowTriggers.NOTE_DELETED]: 'A note is deleted',
};

export const TRIGGER_DESCRIPTIONS: Record<GLMKiloFlowTriggers, string> = {
  [GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT]:
    'Lifecycle: when a contact moves to the chosen pipeline stage, links it to a Kilo athlete (creating one if needed) and sets the athlete status.',
  [GLMKiloFlowTriggers.TAG_ADDED]:
    'Lifecycle: when the chosen tag is added to a contact, links it to a Kilo athlete (creating one if needed) and sets the athlete status.',
  [GLMKiloFlowTriggers.CONTACT_UPDATED]:
    "Data sync (continuous): when a GLM contact's data changes, fills empty fields on the linked Kilo athlete. Only runs for already-linked athletes; email is never synced.",
  [GLMKiloFlowTriggers.NOTE_UPSERTED]:
    'When a note is created or updated on a linked contact, mirrors it onto the Kilo athlete.',
  [GLMKiloFlowTriggers.NOTE_DELETED]:
    'When a note is deleted from a linked contact, removes the mirrored note from the Kilo athlete.',
};

export const ACTION_LABELS: Record<GLMKiloFlowActions, string> = {
  [GLMKiloFlowActions.LINK_ATHLETE_AND_SET_STATUS]: 'Link athlete and set status',
  [GLMKiloFlowActions.SYNC_ATHLETE_DATA]: 'Sync athlete data',
  [GLMKiloFlowActions.UPSERT_NOTE]: 'Create or update note',
  [GLMKiloFlowActions.DELETE_NOTE]: 'Delete note',
};

export const TRIGGER_GROUPS: { label: string; triggers: GLMKiloFlowTriggers[] }[] = [
  {
    label: 'Lifecycle',
    triggers: [GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT, GLMKiloFlowTriggers.TAG_ADDED],
  },
  { label: 'Data Sync', triggers: [GLMKiloFlowTriggers.CONTACT_UPDATED] },
  { label: 'Notes', triggers: [GLMKiloFlowTriggers.NOTE_UPSERTED, GLMKiloFlowTriggers.NOTE_DELETED] },
];

// Athlete statuses an athlete can be set to (mirrors AthleteStatuses on the backend).
export const ATHLETE_STATUSES = [
  'Prospect',
  'Visitor',
  'Trial',
  'Non-Paying Member',
  'Member',
  'Alumni',
];

export interface GLMKiloFormValues {
  glmKiloFlowId: string;
  trigger: GLMKiloFlowTriggers | '';
  tcPipelineId: string | null;
  tcStageId: string | null;
  tcTag: string | null;
  acAthleteStatus: string | null;
  enabled: boolean;
}
